import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { HospitolSetupStatusBanner } from "@/components/setup/HospitolSetupStatusBanner";
import {
  getEstablishmentProfile,
  getHospitalRevenueSettings,
  getPharmacySettings,
  getReceptionSettings,
  ensureSurgeryMedicalUnit,
  hasSurgeryMedicalUnit,
  listMedicalUnits,
  previewNextInvoiceNumber,
  saveEstablishmentProfile,
  saveHospitalRevenueSettings,
  savePharmacySettings,
  saveReceptionSettings,
} from "@/core/admin/establishment-store";
import type { SupportedCurrency } from "@/core/admin/types";
import { listHospitalPartnerships } from "@/core/insurance/partnership-store";
import {
  completeHospitolSetup,
  fetchHospitolSetupStatus,
  skipInsuranceSetupStep,
  updateSetupProgress,
} from "@/core/setup/hospitol-setup-store";
import { routes } from "@/router/all_routes";

const STEPS = [
  { id: 1, label: "Établissement" },
  { id: 2, label: "Services" },
  { id: 3, label: "Accueil & tarifs" },
  { id: 4, label: "Pharmacie" },
  { id: 5, label: "Assurance" },
  { id: 6, label: "Terminé" },
] as const;

const CURRENCIES: SupportedCurrency[] = ["CDF", "USD", "EUR"];

function SetupStepNav({
  currentStep,
  onSelect,
}: {
  currentStep: number;
  onSelect: (step: number) => void;
}) {
  return (
    <nav className="d-flex flex-wrap gap-2 mb-4" aria-label="Étapes mise en route">
      {STEPS.map((step) => (
        <button
          key={step.id}
          type="button"
          className={`btn btn-sm ${currentStep === step.id ? "btn-primary" : "btn-outline-secondary"}`}
          onClick={() => onSelect(step.id)}
        >
          {step.id}. {step.label}
        </button>
      ))}
    </nav>
  );
}

export function HospitolSetupWizardPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [refreshKey, setRefreshKey] = useState(0);
  const [msg, setMsg] = useState<string | null>(null);

  const stepFromUrl = Number(searchParams.get("step") ?? "1");
  const currentStep = Number.isFinite(stepFromUrl)
    ? Math.min(6, Math.max(1, stepFromUrl))
    : 1;

  const status = useMemo(() => {
    void refreshKey;
    return fetchHospitolSetupStatus();
  }, [refreshKey]);

  const profile = useMemo(() => getEstablishmentProfile(), [refreshKey]);
  const pharmacy = useMemo(() => getPharmacySettings(), [refreshKey]);
  const reception = useMemo(() => getReceptionSettings(), [refreshKey]);
  const hospitalRevenue = useMemo(() => getHospitalRevenueSettings(), [refreshKey]);
  const units = useMemo(() => listMedicalUnits(), [refreshKey]);
  const partnerships = useMemo(
    () => listHospitalPartnerships({ status: "active" }),
    [refreshKey],
  );

  const [name, setName] = useState(profile.name);
  const [address, setAddress] = useState(profile.address);
  const [phone, setPhone] = useState(profile.phone);
  const [email, setEmail] = useState(profile.email);
  const [currency, setCurrency] = useState(pharmacy.currency);
  const [invoicePrefix, setInvoicePrefix] = useState(pharmacy.invoicePrefix);
  const [allowReceptionProforma, setAllowReceptionProforma] = useState(
    reception.allowReceptionConsultationProforma,
  );
  const [consultationFee, setConsultationFee] = useState(String(reception.generalConsultationFee));
  const [emergencyVisitFee, setEmergencyVisitFee] = useState(
    String(hospitalRevenue.emergencyVisitFee),
  );
  const [hospitalizationDailyFee, setHospitalizationDailyFee] = useState(
    String(hospitalRevenue.hospitalizationDailyFee),
  );
  const [nursingCareFee, setNursingCareFee] = useState(String(hospitalRevenue.nursingCareFee));
  const [surgeryProcedureFee, setSurgeryProcedureFee] = useState(
    String(hospitalRevenue.surgeryProcedureFee),
  );
  const [ambulanceTransportFee, setAmbulanceTransportFee] = useState(
    String(hospitalRevenue.ambulanceTransportFee),
  );

  useEffect(() => {
    updateSetupProgress(currentStep);
  }, [currentStep]);

  useEffect(() => {
    setName(profile.name);
    setAddress(profile.address);
    setPhone(profile.phone);
    setEmail(profile.email);
    setCurrency(pharmacy.currency);
    setInvoicePrefix(pharmacy.invoicePrefix);
    setAllowReceptionProforma(reception.allowReceptionConsultationProforma);
    setConsultationFee(String(reception.generalConsultationFee));
    setEmergencyVisitFee(String(hospitalRevenue.emergencyVisitFee));
    setHospitalizationDailyFee(String(hospitalRevenue.hospitalizationDailyFee));
    setNursingCareFee(String(hospitalRevenue.nursingCareFee));
    setSurgeryProcedureFee(String(hospitalRevenue.surgeryProcedureFee));
    setAmbulanceTransportFee(String(hospitalRevenue.ambulanceTransportFee));
  }, [profile, pharmacy, reception, hospitalRevenue]);

  const goToStep = (step: number) => {
    setSearchParams(step > 1 ? { step: String(step) } : {});
  };

  const flash = (text: string) => {
    setMsg(text);
    window.setTimeout(() => setMsg(null), 2800);
  };

  const refresh = () => setRefreshKey((k) => k + 1);

  const handleProfileSave = (e: FormEvent) => {
    e.preventDefault();
    saveEstablishmentProfile({ name, address, phone, email });
    flash("Profil enregistré.");
    refresh();
    goToStep(2);
  };

  const handleReceptionSave = (e: FormEvent) => {
    e.preventDefault();
    saveReceptionSettings({
      allowReceptionConsultationProforma: allowReceptionProforma,
      generalConsultationFee: Math.max(0, Number(consultationFee) || 0),
    });
    saveHospitalRevenueSettings({
      emergencyVisitFee: Math.max(0, Number(emergencyVisitFee) || 0),
      hospitalizationDailyFee: Math.max(0, Number(hospitalizationDailyFee) || 0),
      nursingCareFee: Math.max(0, Number(nursingCareFee) || 0),
      surgeryProcedureFee: Math.max(0, Number(surgeryProcedureFee) || 0),
      ambulanceTransportFee: Math.max(0, Number(ambulanceTransportFee) || 0),
    });
    flash("Tarifs accueil et revenus hospitaliers enregistrés.");
    refresh();
    goToStep(4);
  };

  const handleEnsureSurgeryUnit = () => {
    ensureSurgeryMedicalUnit();
    flash("Unité Chirurgie / bloc ajoutée.");
    refresh();
  };

  const handlePharmacySave = (e: FormEvent) => {
    e.preventDefault();
    savePharmacySettings({
      ...pharmacy,
      currency,
      invoicePrefix,
    });
    flash("Paramètres pharmacie enregistrés.");
    refresh();
    goToStep(5);
  };

  const handleSkipInsurance = () => {
    skipInsuranceSetupStep();
    flash("Étape assurance reportée — vous pourrez configurer un partenariat plus tard.");
    refresh();
    goToStep(6);
  };

  const handleComplete = () => {
    const latest = fetchHospitolSetupStatus();
    const missing = Object.entries(latest.checks)
      .filter(([, ok]) => !ok)
      .map(([key]) => key);
    if (missing.includes("establishment") || missing.includes("reception") || missing.includes("pharmacy")) {
      flash("Complétez les étapes obligatoires avant de finaliser.");
      return;
    }
    completeHospitolSetup();
    flash("Mise en route finalisée — bon accueil dans ITaaS Hospitol !");
    navigate(routes.admissions);
  };

  return (
    <div>
      <PageHeader
        title="Mise en route"
        description="Assistant guidé pour configurer votre clinique ou hôpital — contexte RDC (FC, Mobile Money, tiers payant)."
        actions={
          <Link to={routes.tenantDashboard} className="btn btn-outline-secondary btn-sm">
            Tableau de bord
          </Link>
        }
      />

      <HospitolSetupStatusBanner status={status} showWhenComplete showWhenIncomplete={false} />

      {msg && <div className="alert alert-success py-2 small">{msg}</div>}

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
            <span className="small text-muted">Progression globale</span>
            <span className="small fw-semibold">{status.progressPercent} %</span>
          </div>
          <div className="progress" style={{ height: 8 }}>
            <div
              className="progress-bar bg-primary"
              style={{ width: `${status.progressPercent}%` }}
            />
          </div>
        </div>
      </div>

      <SetupStepNav currentStep={currentStep} onSelect={goToStep} />

      {currentStep === 1 && (
        <div className="card border-0 shadow-sm">
          <div className="card-body">
            <h3 className="h6 fw-semibold mb-3">1 — Profil de l&apos;établissement</h3>
            <p className="small text-muted mb-3">
              Nom, adresse et contacts affichés sur les factures et documents patients.
            </p>
            <form onSubmit={handleProfileSave} className="row g-3">
              <div className="col-12 col-md-6">
                <label className="form-label small">Nom</label>
                <input className="form-control form-control-sm" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label small">Téléphone</label>
                <input className="form-control form-control-sm" value={phone} onChange={(e) => setPhone(e.target.value)} required />
              </div>
              <div className="col-12">
                <label className="form-label small">Adresse</label>
                <input className="form-control form-control-sm" value={address} onChange={(e) => setAddress(e.target.value)} required />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label small">E-mail</label>
                <input type="email" className="form-control form-control-sm" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div className="col-12">
                <button type="submit" className="btn btn-primary btn-sm">
                  Enregistrer et continuer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {currentStep === 2 && (
        <div className="card border-0 shadow-sm">
          <div className="card-body">
            <h3 className="h6 fw-semibold mb-3">2 — Services &amp; files d&apos;attente</h3>
            <p className="small text-muted mb-3">
              Unités médicales utilisées pour l&apos;orientation depuis l&apos;accueil (Médecine générale,
              Urgences, Chirurgie / bloc, Labo, Pharmacie…).
            </p>
            {status.checks.medicalUnits ? (
              <div className="alert alert-success small py-2">
                {units.filter((u) => u.active).length} service(s) actif(s) configuré(s).
              </div>
            ) : (
              <div className="alert alert-warning small py-2">
                Au moins 2 services actifs requis. Les unités démo sont créées automatiquement au premier
                accès.
              </div>
            )}
            {status.checks.surgeryUnit ? (
              <div className="alert alert-success small py-2">
                Unité <strong>Chirurgie / bloc</strong> active — orientation bloc et facturation auto OK.
              </div>
            ) : (
              <div className="alert alert-warning small py-2">
                Ajoutez une unité <strong>Chirurgie / bloc</strong> pour orienter vers le bloc opératoire.
              </div>
            )}
            <ul className="small mb-3">
              {units.slice(0, 10).map((u) => (
                <li key={u.id}>
                  {u.name} {u.active ? "" : "(inactif)"}
                </li>
              ))}
            </ul>
            <div className="d-flex gap-2 flex-wrap">
              {!hasSurgeryMedicalUnit() && (
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={handleEnsureSurgeryUnit}>
                  Ajouter Chirurgie / bloc
                </button>
              )}
              <button type="button" className="btn btn-primary btn-sm" onClick={() => goToStep(3)}>
                Continuer
              </button>
              <Link to={routes.adminSettings} className="btn btn-outline-secondary btn-sm">
                Gérer les unités
              </Link>
            </div>
          </div>
        </div>
      )}

      {currentStep === 3 && (
        <div className="card border-0 shadow-sm">
          <div className="card-body">
            <h3 className="h6 fw-semibold mb-3">3 — Accueil &amp; tarifs</h3>
            <p className="small text-muted mb-3">
              Consultation accueil et forfaits hospitaliers (urgences, séjour, soins, bloc, ambulance) —
              devise établissement.
            </p>
            <form onSubmit={handleReceptionSave} className="row g-3">
              <div className="col-12">
                <div className="small fw-semibold text-muted mb-1">Accueil</div>
              </div>
              <div className="col-12 col-sm-6 col-md-4">
                <label className="form-label small">Tarif consultation (FC)</label>
                <input
                  type="number"
                  min={0}
                  className="form-control form-control-sm"
                  value={consultationFee}
                  onChange={(e) => setConsultationFee(e.target.value)}
                  required
                />
              </div>
              <div className="col-12">
                <div className="form-check">
                  <input
                    id="allow-proforma"
                    type="checkbox"
                    className="form-check-input"
                    checked={allowReceptionProforma}
                    onChange={(e) => setAllowReceptionProforma(e.target.checked)}
                  />
                  <label htmlFor="allow-proforma" className="form-check-label small">
                    Autoriser la proforma consultation à l&apos;accueil
                  </label>
                </div>
              </div>
              <div className="col-12 mt-2">
                <div className="small fw-semibold text-muted mb-1">Revenus hospitaliers</div>
              </div>
              <div className="col-12 col-sm-6 col-md-4">
                <label className="form-label small">Passage urgences</label>
                <input
                  type="number"
                  min={0}
                  className="form-control form-control-sm"
                  value={emergencyVisitFee}
                  onChange={(e) => setEmergencyVisitFee(e.target.value)}
                />
              </div>
              <div className="col-12 col-sm-6 col-md-4">
                <label className="form-label small">Hospitalisation (journée)</label>
                <input
                  type="number"
                  min={0}
                  className="form-control form-control-sm"
                  value={hospitalizationDailyFee}
                  onChange={(e) => setHospitalizationDailyFee(e.target.value)}
                />
              </div>
              <div className="col-12 col-sm-6 col-md-4">
                <label className="form-label small">Soins infirmiers (acte)</label>
                <input
                  type="number"
                  min={0}
                  className="form-control form-control-sm"
                  value={nursingCareFee}
                  onChange={(e) => setNursingCareFee(e.target.value)}
                />
              </div>
              <div className="col-12 col-sm-6 col-md-4">
                <label className="form-label small">Chirurgie / bloc (forfait)</label>
                <input
                  type="number"
                  min={0}
                  className="form-control form-control-sm"
                  value={surgeryProcedureFee}
                  onChange={(e) => setSurgeryProcedureFee(e.target.value)}
                />
              </div>
              <div className="col-12 col-sm-6 col-md-4">
                <label className="form-label small">Transport ambulance</label>
                <input
                  type="number"
                  min={0}
                  className="form-control form-control-sm"
                  value={ambulanceTransportFee}
                  onChange={(e) => setAmbulanceTransportFee(e.target.value)}
                />
              </div>
              <div className="col-12">
                <button type="submit" className="btn btn-primary btn-sm">
                  Enregistrer et continuer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {currentStep === 4 && (
        <div className="card border-0 shadow-sm">
          <div className="card-body">
            <h3 className="h6 fw-semibold mb-3">4 — Pharmacie &amp; facturation</h3>
            <form onSubmit={handlePharmacySave} className="row g-3">
              <div className="col-12 col-sm-6 col-md-4">
                <label className="form-label small">Devise</label>
                <select
                  className="form-select form-select-sm"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value as SupportedCurrency)}
                >
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-12 col-sm-6 col-md-4">
                <label className="form-label small">Préfixe facture</label>
                <input
                  className="form-control form-control-sm"
                  value={invoicePrefix}
                  onChange={(e) => setInvoicePrefix(e.target.value)}
                  required
                />
              </div>
              <div className="col-12 col-sm-6 col-md-4">
                <label className="form-label small">Prochain numéro</label>
                <input className="form-control form-control-sm" value={previewNextInvoiceNumber()} readOnly />
              </div>
              <div className="col-12">
                <button type="submit" className="btn btn-primary btn-sm">
                  Enregistrer et continuer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {currentStep === 5 && (
        <div className="card border-0 shadow-sm">
          <div className="card-body">
            <h3 className="h6 fw-semibold mb-3">5 — Assurance &amp; partenariats hub</h3>
            <p className="small text-muted mb-3">
              Connectez au moins une mutuelle (ex. Mutuelle Salama) pour le tiers payant et la publication
              automatique des factures assureur.
            </p>
            {partnerships.length > 0 ? (
              <div className="alert alert-success small py-2 mb-3">
                {partnerships.length} partenariat(s) actif(s) :{" "}
                {partnerships.map((p) => p.hospitalName).join(", ")}
              </div>
            ) : (
              <div className="alert alert-warning small py-2 mb-3">
                Aucun partenariat actif. Vous pouvez continuer et configurer plus tard.
              </div>
            )}
            <div className="d-flex gap-2 flex-wrap">
              <Link to={routes.insurancePartnerships} className="btn btn-primary btn-sm">
                Configurer les partenariats
              </Link>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => goToStep(6)}>
                Continuer
              </button>
              <button type="button" className="btn btn-link btn-sm" onClick={handleSkipInsurance}>
                Passer cette étape
              </button>
            </div>
          </div>
        </div>
      )}

      {currentStep === 6 && (
        <div className="card border-0 shadow-sm">
          <div className="card-body">
            <h3 className="h6 fw-semibold mb-3">6 — C&apos;est prêt !</h3>
            <p className="small text-muted mb-3">
              Enregistrez un premier patient à l&apos;accueil, orientez-le vers la consultation ou la
              pharmacie, et testez le parcours complet (assuré ou cash).
            </p>
            <ul className="small mb-4">
              <li className={status.checks.establishment ? "text-success" : "text-danger"}>
                Profil établissement
              </li>
              <li className={status.checks.medicalUnits ? "text-success" : "text-danger"}>
                Services médicaux
              </li>
              <li className={status.checks.surgeryUnit ? "text-success" : "text-warning"}>
                Unité Chirurgie / bloc
              </li>
              <li className={status.checks.reception ? "text-success" : "text-danger"}>
                Tarifs accueil
              </li>
              <li className={status.checks.hospitalRevenue ? "text-success" : "text-warning"}>
                Tarifs revenus hospitaliers
              </li>
              <li className={status.checks.pharmacy ? "text-success" : "text-danger"}>
                Pharmacie &amp; facturation
              </li>
              <li className={status.checks.insurance ? "text-success" : "text-warning"}>
                Assurance (optionnel)
              </li>
            </ul>
            <div className="d-flex gap-2 flex-wrap">
              <button type="button" className="btn btn-success btn-sm" onClick={handleComplete}>
                Finaliser la mise en route
              </button>
              <Link to={routes.admissions} className="btn btn-outline-primary btn-sm">
                Ouvrir l&apos;accueil
              </Link>
              <Link to={routes.adminSettings} className="btn btn-outline-secondary btn-sm">
                Paramètres avancés
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
